import { camelCase, toLower, replace, startCase } from 'lodash';

import {
  BASIS_OF_COMPARISON_OPTIONS,
  EXPENSE_COMPARABLES_CATEGORIES,
  EXPENSE_RATIO,
  TOTAL_OPERATING_EXPENSES,
  TOTAL_OPERATING_EXPENSES_PER_SF,
  TOTAL_OPERATING_EXPENSES_PER_UNIT,
} from 'shared/constants/expenses';
import { divide } from 'shared/utils/numberOperations';

import {
  formatCurrencyFloat,
  formatCurrencyInt,
  formatInt,
  formatPercentageString,
} from 'shared/utils/numberFormatters';
import { ExpenseComp } from 'types/underwriting/income-approach/expenses';

export const getValuePerBasis = (basis: string | undefined, value: number, comp?: ExpenseComp | any): number => {
  let divider: number | undefined = undefined;
  switch (basis) {
    case BASIS_OF_COMPARISON_OPTIONS.PER_SF:
      divider = comp?.grossBuildingArea;
      break;
    case BASIS_OF_COMPARISON_OPTIONS.PER_UNIT:
      divider = comp?.residentialUnits;
      break;
    case BASIS_OF_COMPARISON_OPTIONS.GROSS:
      divider = 1;
      break;
    default:
      return 0;
  }
  return divide(value, divider);
};

const formatId = (name: string) => {
  return camelCase(toLower(replace(name, '&', 'And')));
};

export const formatExpenseId = (expenseName: string) => {
  const id = formatId(expenseName);
  return id;
};

export const getValueFormatter = (rowKey: string, basis: string): ((value: any) => any) => {
  switch (rowKey) {
    case EXPENSE_COMPARABLES_CATEGORIES.address:
    case EXPENSE_COMPARABLES_CATEGORIES.sourceOfInformation:
    case EXPENSE_COMPARABLES_CATEGORIES.city:
    case EXPENSE_COMPARABLES_CATEGORIES.state:
    case EXPENSE_COMPARABLES_CATEGORIES.expensePeriod:
    case EXPENSE_COMPARABLES_CATEGORIES.expenseMonth:
    case EXPENSE_COMPARABLES_CATEGORIES.propertyType:
      return startCase;
    case EXPENSE_COMPARABLES_CATEGORIES.grossBuildingArea:
    case EXPENSE_COMPARABLES_CATEGORIES.residentialUnits:
      return formatInt;
    case EXPENSE_COMPARABLES_CATEGORIES.effectiveGrossIncome:
    case TOTAL_OPERATING_EXPENSES.key:
    case TOTAL_OPERATING_EXPENSES_PER_UNIT.key:
      return formatCurrencyInt;
    case TOTAL_OPERATING_EXPENSES_PER_SF.key:
      return formatCurrencyFloat;
    case EXPENSE_RATIO.key:
      return formatPercentageString;
    case EXPENSE_COMPARABLES_CATEGORIES.realEstateTaxes:
    case EXPENSE_COMPARABLES_CATEGORIES.insurance:
    case EXPENSE_COMPARABLES_CATEGORIES.utilities:
    case EXPENSE_COMPARABLES_CATEGORIES.electricity:
    case EXPENSE_COMPARABLES_CATEGORIES.fuel:
    case EXPENSE_COMPARABLES_CATEGORIES.waterAndSewer:
    case EXPENSE_COMPARABLES_CATEGORIES.repairsAndMaintenance:
    case EXPENSE_COMPARABLES_CATEGORIES.payrollAndBenefits:
    case EXPENSE_COMPARABLES_CATEGORIES.generalAndAdministrative:
    case EXPENSE_COMPARABLES_CATEGORIES.legalAndProfessionalFees:
    case EXPENSE_COMPARABLES_CATEGORIES.miscellaneous:
    case EXPENSE_COMPARABLES_CATEGORIES.managementFees:
    case EXPENSE_COMPARABLES_CATEGORIES.replacementReserves:
      return basis === BASIS_OF_COMPARISON_OPTIONS.PER_SF ? formatCurrencyFloat : formatCurrencyInt;
    case EXPENSE_COMPARABLES_CATEGORIES.expenseYear:
    case EXPENSE_COMPARABLES_CATEGORIES.yearBuilt:
      return (value) => value?.toString();
    default:
      return basis === BASIS_OF_COMPARISON_OPTIONS.PER_SF ? formatCurrencyFloat : formatCurrencyInt;
  }
};
